import { PropsWithChildren, FC } from 'react'
import { Row, Col, Skeleton } from 'antd'
import styles from '@OpsQuality/styles.css'
import { getProgressBarData } from '@ProdWb/JobDetails/logic'
import { JobRecordType } from '@ProdWb/JobList/types'
import JobProgressBarCollapsable from '@ProdWb/JobDetails/components/JobProgressCollapsable'
import { DEFAULT_BUSINESS_UNIT } from '@OpsWb/constants'

import Box from '~/core/Box'
import Link from '~/core/Link'
import SecurePlusIcon from '~/components/SecurePlusIcon'
import { OPS_PRODUCTION_PATH } from '~/redux-modules/preferences'
import colors from '~/styles/_colors.css'
import { adminJobRequestQuery_jobRequest as JobRequestType } from '../__generated__/adminJobRequestQuery'
import { Job, useJobDetailsQuery, useJobMilestonesQuery } from '~/gql/graphql'

import { isAttachmentsAvailableForCustomerView } from './logic'

interface OverviewTabPropsType {
    jobRequest?: JobRequestType | null
}

const OverviewTab: FC<PropsWithChildren<OverviewTabPropsType>> = props => {
    const { jobRequest } = props

    const isDownloadInspectionDocsBtnEnabled =
        isAttachmentsAvailableForCustomerView(jobRequest?.parts)
    const { data: jobData, loading } = useJobDetailsQuery({
        variables: {
            id: Number(jobRequest?.jobId),
            mfgPartnerId: null,
            schedulingQueueItemId: null,
        },
        fetchPolicy: 'network-only',
    })
    const job = jobData?.job as Job

    const { data: milestonesData } = useJobMilestonesQuery({
        variables: {
            id: job?.id,
        },
        skip: !job?.id,
    })

    const progressBarData = getProgressBarData(
        milestonesData?.jobMilestones,
        job?.salesOrderCreatedAt,
    )

    const jobDataForCreateException = {
        id: job?.id,
        key: job?.id,
        name: job?.name,
        saleOrderId: job?.erpSaleOrderId,
        salesOrderName: job?.salesOrderName,
    } as JobRecordType

    return (
        <>
            <Box
                className={styles.progressBar}
                borderColor={colors.topNavHeaderBorder}
                direction="col"
                key={job?.id as number}
            >
                <Skeleton loading={loading}>
                    <JobProgressBarCollapsable
                        exceptionCount={job?.exceptionCount || 0}
                        jobName={job?.name || ''}
                        jobId={job?.id || 0}
                        jobRequestId={jobRequest?.id}
                        jobStatus={job?.status || ''}
                        progressData={progressBarData}
                        pauseException={job?.pauseException}
                        jobDataForCreateException={
                            jobDataForCreateException as JobRecordType
                        }
                        isDownloadInspectionDocsBtnEnabled={
                            isDownloadInspectionDocsBtnEnabled
                        }
                    />
                </Skeleton>
            </Box>
            <Box className={styles.overviewCard} direction="col">
                <Row className={styles.overviewCardTitle}>
                    <Col span={3}>
                        <Box className={styles.overviewTitle}>Sales order</Box>
                        <Box className={styles.overviewContent}>
                            <Link
                                name="order-name"
                                target="_blank"
                                href={`${OPS_PRODUCTION_PATH}/sales-order-details/${jobRequest?.salesOrderName}`}
                            >
                                {jobRequest?.salesOrderName}
                            </Link>
                        </Box>
                    </Col>
                    <Col span={3}>
                        <Box className={styles.overviewTitle}>
                            Business unit
                        </Box>
                        <Box className={styles.overviewContent}>
                            {job?.businessUnit || DEFAULT_BUSINESS_UNIT}
                        </Box>
                    </Col>
                    <Col span={3}>
                        <Box className={styles.overviewTitle}>Priority</Box>
                        <Box className={styles.overviewContent}>
                            {job?.priority}
                        </Box>
                    </Col>
                    <Col span={3}>
                        <Box className={styles.overviewTitle}>
                            Customer/Company
                        </Box>
                        <Box className={styles.overviewContent}>
                            {job?.customerName && job.customerName}
                            {job?.customerCompanyName &&
                                ` / ${job.customerCompanyName}`}
                        </Box>
                    </Col>
                    <Col span={3}>
                        <Box className={styles.overviewTitle}>Sales DRI</Box>
                        <Box className={styles.overviewContent}>
                            {job?.customerMfgDri?.displayName}
                        </Box>
                    </Col>
                    <Col span={3}>
                        <Box className={styles.overviewTitle}>MFG DRI</Box>
                        <Box className={styles.overviewContent}>
                            {job?.mfgDri?.displayName}
                        </Box>
                    </Col>
                    <Col span={3}>
                        <Box className={styles.overviewTitle}>Project name</Box>
                        <Box className={styles.overviewContent}>
                            {jobRequest?.project?.isSecurePlus && (
                                <SecurePlusIcon
                                    iconSize="16px"
                                    iconColor={colors.success}
                                />
                            )}
                            {jobRequest?.project?.name}
                        </Box>
                    </Col>
                </Row>
            </Box>
        </>
    )
}

export default OverviewTab
